import * as Sentry from "@sentry/react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { ErrorBoundary } from "react-error-boundary";
import { MapProvider } from "react-map-gl/maplibre";
import { Provider } from "react-redux";
import { PersistGate } from "redux-persist/integration/react";
import App from "./components/App.tsx";
import CustomMantineProvider from "./components/CustomMantineProvider.tsx";
import FirebaseProvider from "./components/FirebaseProvider.tsx";
import AdminStatusProvider from "./contexts/AdminStatusProvider.tsx";
import { persistor, store } from "./store/store.ts";
import {
  bootMarkEnd,
  bootMarkStart,
  initBootPerf,
} from "./utils/boot-perf.ts";
import { isDevMode } from "./utils/debug.ts";
import { notifySentryIntegrationsReady } from "./utils/sentry-deferred.ts";

// NB: ES imports hoist, so by the time this line runs every import above has
// already been fetched, parsed and evaluated. That is exactly what we want to
// measure - index.html marks the start, this closes it.
initBootPerf();
bootMarkEnd("bundle-eval");

// Create React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

declare global {
  interface Window {
    __TANSTACK_QUERY_CLIENT__: import("@tanstack/query-core").QueryClient;
  }
}

if (!import.meta.env.DEV) {
  Sentry.init({
    dsn: "https://aa73d6913c5c048361875d330ae7e5e3@o4508519639220224.ingest.us.sentry.io/4508519656128512",
    // Only browserTracing is loaded up front. Replay (~121KB) and feedback
    // (~37KB) are added after the page is interactive - see below - so they
    // cost nothing before first paint.
    integrations: [Sentry.browserTracingIntegration()],
    // Sampling 100% of transactions costs every user a full trace payload and
    // burns quota; 10% is plenty of signal for a site this size.
    tracesSampleRate: 0.1,
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: ["localhost", /^https:\/\/api\.iwanna\.camp/],
    // Session Replay
    replaysSessionSampleRate: 0.1, // 10% of sessions
    replaysOnErrorSampleRate: 1.0, // always replay a session that errored
    environment: "production",
    tunnel: "/tunnel/",
    release: "iwannacamp@" + import.meta.env.VITE_APP_VERSION?.substring(0, 7),
  });

  // Defer the heavy integrations until the browser is idle. lazyLoadIntegration
  // fetches them from Sentry's CDN at runtime, so unlike a dynamic import() of
  // "@sentry/react" - which resolves to the module already in the static graph,
  // and therefore bundles replay + feedback anyway - this keeps ~158KB of
  // replay/feedback out of the entry chunk entirely. Errors thrown before this
  // resolves are still captured; they just carry no replay.
  const loadDeferredSentry = async () => {
    const client = Sentry.getClient();
    if (!client) return;
    try {
      const [replay, feedback] = await Promise.all([
        Sentry.lazyLoadIntegration("replayIntegration"),
        Sentry.lazyLoadIntegration("feedbackIntegration"),
      ]);
      client.addIntegration(replay());
      client.addIntegration(
        feedback({ colorScheme: "system", autoInject: false })
      );
    } catch {
      // Telemetry is best-effort - a blocked CDN must not surface to the user.
    } finally {
      // Always signal, so consumers waiting on feedback stop waiting either way.
      notifySentryIntegrationsReady();
    }
  };
  if ("requestIdleCallback" in window) {
    requestIdleCallback(() => void loadDeferredSentry(), { timeout: 5000 });
  } else {
    setTimeout(() => void loadDeferredSentry(), 2000);
  }
} else {
  // Dev-only. A static import shipped ~211KB of react-scan to production even
  // though scan() was never called there.
  void import("react-scan").then(({ scan }) =>
    scan({
      enabled: false,
      showToolbar: true,
      trackUnnecessaryRenders: true,
    })
  );
}

const EnvErrorBoundary = (props: { children: React.ReactNode }) => {
  if (!isDevMode()) {
    return (
      <Sentry.ErrorBoundary
        fallback={({ error, resetError, componentStack }) => (
          <div>
            <p>Something went wrong:</p>
            <pre>{(error as any).toString()}</pre>
            <div>{componentStack}</div>
            <button onClick={resetError}>Try again</button>
          </div>
        )}
      >
        {props.children}
      </Sentry.ErrorBoundary>
    );
  } else {
    return (
      <ErrorBoundary
        fallbackRender={({ error, resetErrorBoundary }) => (
          <div>
            <p>Something went wrong:</p>
            <pre>{error instanceof Error ? error.message : String(error)}</pre>
            <button onClick={resetErrorBoundary}>Try again</button>
          </div>
        )}
      >
        {props.children}
      </ErrorBoundary>
    );
  }
};

bootMarkStart("react-mount");

createRoot(document.getElementById("root")!).render(
  <EnvErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <Provider store={store}>
        <PersistGate loading={null} persistor={persistor}>
          <FirebaseProvider>
            <AdminStatusProvider>
              <MapProvider>
                <StrictMode>
                  <CustomMantineProvider>
                    <App />
                  </CustomMantineProvider>
                </StrictMode>
              </MapProvider>
            </AdminStatusProvider>
          </FirebaseProvider>
        </PersistGate>
      </Provider>
    </QueryClientProvider>
  </EnvErrorBoundary>
);

bootMarkEnd("react-mount");
